import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Button, Divider, FormField, FormGroup, FormRadio, Header, Icon, Input, Modal, ModalActions, ModalContent, Select, TextArea } from 'semantic-ui-react';
import { getProducts } from '../../../../services/products.service';
import { getUsers } from '../../../../services/users.service';
import { USER_ROLE_ADVISOR } from '../../../../utils/constants';
import { createProspect } from '../../../../services/prospects.service';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../hooks/AuthProvider';
import DropdownOption from '../../../../types/models/DropdownOption';
import { AGE_RANGES_OPTIONS, GENDER_OPTIONS, MONTH_OPTIONS, PROSPECT_ORIGIN_OPTIONS } from '../../../../utils/dropdownOptions';
import SearchClientDropdown from '../../../../components/SearchClientDropdown';
import DatePicker from 'react-datepicker';
import { setHours, setMinutes } from 'date-fns';
import { getClientAddressesByClientId } from '../../../../services/clients.service';
import { getSettlementsByZipCode } from '../../../../services/sepomex.service';

interface ProspectData {
    origin: string;
    advisorId: string;
    productId: string;
    notes: string;
}

interface ProspectPersonData {
    name: string;
    paternalSurname: string;
    maternalSurname: string;
    gender: string;
    age: string;
    phone: string;
    birthDay: string;
    birthMonth: string;
    birthYear: string;
}

interface ProspectAddressData {
    street?: string;
    interiorNumber?: string;
    exteriorNumber?: string;
    postalCode?: string;
    city?: string;
    municipality?: string;
    state?: string;
    neighborhood?: string;
}

interface AppointmentData {
    addressType: string;
    dateTime: Date;
    selectedAddressId?: string;
    addressData?: ProspectAddressData;
}

interface ProspectRequest {
    prospectData: ProspectData;
    personType: string;
    selectedClientId?: string;
    personData?: ProspectPersonData;
    appointmentData: AppointmentData;
}

interface CreateNewProspectModalProps {
    onProspectCreated: () => void
}

const CreateNewProspectModal = ({
    onProspectCreated
}: CreateNewProspectModalProps) => {

    const auth = useAuth();

    const userLogged = auth.userData ?? null;
    const userIsAdvisor = userLogged?.role === USER_ROLE_ADVISOR;

    const [openModal, setOpenModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [advisors, setAdvisors] = useState<DropdownOption[]>([]);
    const [products, setProducts] = useState<DropdownOption[]>([]);
    const [neighborhoods, setNeighborhoods] = useState<DropdownOption[]>([]);
    const [clientAddresses, setClientAddresses] = useState<DropdownOption[]>([]);

    const [clientRadioSelected, setClientRadioSelected] = useState<string | undefined>(undefined);
    const [addressRadioSelected, setAddressRadioSelected] = useState<string | undefined>(undefined);

    const [clientIdSelected, setClientIdSelected] = useState<string>("");
    const [addressIdSelected, setAddressIdSelected] = useState<string>("");

    const [appointmentDateTime, setAppointmentDateTime] = useState<Date | null>(null);

    const [requestProspectData, setRequestProspectData] = useState<ProspectData>({
        origin: "",
        advisorId: userIsAdvisor ? userLogged?.id : "",
        productId: "",
        notes: "",
    });

    const [requestPersonData, setRequestPersonData] = useState<ProspectPersonData>({
        name: "",
        paternalSurname: "",
        maternalSurname: "",
        gender: "",
        age: "",
        phone: "",
        birthDay: "",
        birthMonth: "",
        birthYear: "",
    });

    const [requestAddressData, setRequestAddressData] = useState<ProspectAddressData>({
        street: "",
        interiorNumber: "",
        exteriorNumber: "",
        postalCode: "",
        city: "",
        municipality: "",
        state: "",
        neighborhood: "",
    });

    const handleChangeProspectData = (e: any, { name, value }: any) => {
        setRequestProspectData({
            ...requestProspectData,
            [name]: value,
        });
    };

    const handleChangePersonData = (e: any, { name, value }: any) => {
        if (name === "phone" && !validatePhone(value)) return;
        setRequestPersonData({
            ...requestPersonData,
            [name]: value,
        });
    };

    const handleChangeAddressData = async (e: any, { name, value }: any) => {
        if (name === "postalCode" && value.length === 5) {
            try {
                const response = await getSettlementsByZipCode(value);
                if (response?.status === 200) {
                    const itemZero = response.data[0];
                    const neighborhoodsList = response.data.map(
                        (neighborhood: any) => {
                            return {
                                key: neighborhood["settlement"],
                                value: neighborhood["settlement"],
                                text: neighborhood["settlement"],
                            };
                        }
                    );
                    setNeighborhoods(neighborhoodsList);

                    setRequestAddressData({
                        ...requestAddressData,
                        postalCode: value ?? "",
                        city: itemZero.city,
                        municipality: itemZero.municipality,
                        state: itemZero.state,
                    });
                } else {
                    throw new Error("El código postal seleccionado no es valido.");
                }
            } catch (error: any) {
                toast.error(error.message);
            }
        } else {
            setRequestAddressData({
                ...requestAddressData,
                [name]: value ?? "",
            });
        }
    };

    const fetchAdvisors = useCallback(async () => {
        try {
            if (userIsAdvisor) {
                const advisorsList = [{
                    key: userLogged?.id,
                    value: userLogged?.id,
                    text: userLogged?.person?.fullName ?? "Sin nombre",
                }];
                setAdvisors(advisorsList);
            } else {
                const response = await getUsers(100, 0, USER_ROLE_ADVISOR);
                if (!response?.data.content) {
                    return;
                }
                const advisorsList = response?.data.content.map(
                    (advisor: any) => {
                        return {
                            key: advisor.id,
                            value: advisor.id,
                            text: advisor.person?.fullName ?? "Sin nombre",
                        };
                    }
                );
                setAdvisors(advisorsList);
            }
        } catch (error) {
            toast.error("Error al obtener la lista de asesores");
        }
    }, [userIsAdvisor, userLogged, setAdvisors]);

    const fetchProducts = async () => {
        try {
            const response = await getProducts(100, 0);
            const productsList = response?.data.content.map(
                (product: any) => {
                    return {
                        key: product["id"],
                        value: product["id"],
                        text: `${product["sku"]} - ${product["name"]}`,
                    };
                }
            );
            setProducts(productsList);
        } catch (error) {
            console.error("Error al obtener la lista de asesores:", error);
        }
    };

    const fetchClientAddresses = async (clientId: string) => {
        try {
            const response = await getClientAddressesByClientId(100, 0, "createdAt,asc", clientId);
            const addressList = response?.data.content.map(
                (item: any) => {
                    const addressLine = `${item["street"]} #${item["exteriorNumber"]}, 
                        ${item["interiorNumber"] === "" ? "" : item["interiorNumber"] + ", "}
                        ${item["neighborhood"]}, ${item["postalCode"]}, 
                        ${item["city"]}, ${item["state"]}`;
                    return {
                        key: item["id"],
                        value: item["id"],
                        text: addressLine,
                    };
                }
            );
            setClientAddresses(addressList);
        } catch (error) {
            console.error("Error al obtener la lista de direcciones de el cliente seleccionado:", error);
        }
    };

    useEffect(() => {
        if (!clientIdSelected) return;
        fetchClientAddresses(clientIdSelected);
    }, [clientIdSelected]);

    const handleSelectedClientAddress = (e: any, { value }: any) => {
        setAddressIdSelected(value);
    }

    useEffect(() => {
        if (openModal) {
            fetchAdvisors();
            fetchProducts();
        }
    }, [fetchAdvisors, openModal]);

    const validatePhone = (phone: string) => {
        const regex = /^[0-9]+$/;
        if (phone.length > 10) {
            return false;
        }
        if (phone !== "" && !regex.test(phone)) {
            return false;
        }
        return true;
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {

            if (!clientRadioSelected) {
                throw new Error("Debe seleccionar un tipo de persona");
            }

            if (!addressRadioSelected) {
                throw new Error("Debe seleccionar un tipo de direccion");
            }

            if (!appointmentDateTime) {
                throw new Error("Debe seleccionar una fecha de la cita");
            }

            // creating the request to create a new prospect with the appointment
            const request: ProspectRequest = {
                prospectData: requestProspectData,
                personType: clientRadioSelected,
                selectedClientId: clientIdSelected,
                personData: clientRadioSelected === "NEW_PROSPECT" ? requestPersonData : undefined,
                appointmentData: {
                    addressType: addressRadioSelected,
                    dateTime: appointmentDateTime,
                    selectedAddressId: addressIdSelected,
                    addressData: addressRadioSelected === "NEW_ADDRESS" ? requestAddressData : undefined
                },
            };

            const response = await createProspect(request);

            if (response?.status === 201) {
                //window.location.href = `/prospects/${response.data.id}/form`;
                // TODO: reload data table and show a success toast
                onProspectCreated();
                toast.success("El prospecto ha sido creado con exito.");
                resetForm();
                setOpenModal(false);
            } else {
                throw new Error("El prospecto no ha sido creado exitosamente, favor de contactar al area de TI");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setRequestProspectData({
            origin: "",
            advisorId: userIsAdvisor ? userLogged?.id : "",
            productId: "",
            notes: "",
        });
        setRequestPersonData({
            name: "",
            paternalSurname: "",
            maternalSurname: "",
            gender: "",
            age: "",
            phone: "",
            birthDay: "",
            birthMonth: "",
            birthYear: "",
        });
        setRequestAddressData({
            street: "",
            interiorNumber: "",
            exteriorNumber: "",
            postalCode: "",
            city: "",
            municipality: "",
            state: "",
            neighborhood: "",
        });
        setClientRadioSelected(undefined);
        setAddressRadioSelected(undefined);
        setClientIdSelected("");
        setAddressIdSelected("");
        setAppointmentDateTime(null);
    };

    const handleClientRadioSelected = (value: string) => {
        setClientRadioSelected(value);
        if (value === "NEW_PROSPECT") {
            setClientIdSelected("");
        }
    };

    const handleAddressRadioSelected = (value: string) => {
        setAddressRadioSelected(value);
        if (value === "NEW_ADDRESS" || value === "SHOWROOM") {
            setAddressIdSelected("");
        }
    };

    const handleOpenModal = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenModal(true);
    };

    const handleCancelModal = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenModal(false);
    };

    return (
        <>
            <Modal
                closeOnDimmerClick
                closeIcon={true}
                size="large"
                onClose={(event) => handleCancelModal(event)}
                open={openModal}
                trigger={
                    <Button floated='right' onClick={(event) => handleOpenModal(event)}>
                        Crear nuevo prospecto
                    </Button>
                } >
                <Header icon="calendar" content="Crear nuevo prospecto" />
                <ModalContent>
                    <div className="ui form">

                        <FormGroup widths="equal">

                            <FormField
                                control={Select}
                                label={<label htmlFor="form-select-control-origin">Canal de origen</label>}
                                options={PROSPECT_ORIGIN_OPTIONS}
                                placeholder="Selecciona una opcion"
                                name="origin"
                                value={requestProspectData.origin}
                                onChange={handleChangeProspectData}
                                required
                                search
                                searchInput={{ id: "form-select-control-origin" }}
                                clearable
                            />

                            {!userIsAdvisor && (
                                <FormField
                                    control={Select}
                                    label={<label htmlFor="form-select-control-advisor">Asesor</label>}
                                    options={advisors}
                                    placeholder="Selecciona una opcion"
                                    name="advisorId"
                                    value={requestProspectData.advisorId}
                                    onChange={handleChangeProspectData}
                                    required
                                    search
                                    searchInput={{ id: "form-select-control-advisor" }}
                                    clearable
                                />
                            )}

                            <FormField
                                control={Select}
                                label={<label htmlFor="form-select-control-advisor">Producto de interes</label>}
                                options={products}
                                placeholder="Selecciona una opcion"
                                name="productId"
                                value={requestProspectData.productId}
                                onChange={handleChangeProspectData}
                                required
                                search
                                searchInput={{ id: "form-select-control-advisor" }}
                                clearable
                            />
                        </FormGroup>

                        <FormField
                            control={TextArea}
                            label="Notas adicionales"
                            placeholder="Añade notas adicionales para el prospecto..."
                            name="notes"
                            value={requestProspectData.notes}
                            onChange={handleChangeProspectData}
                        />

                        <Divider hidden />
                        <Divider horizontal>
                            <Header as='h5'>
                                Selecciona si es un nuevo prospecto o cliente existente
                            </Header>
                        </Divider>

                        <FormGroup>
                            <FormRadio
                                label='Nuevo prospecto'
                                value='NEW_PROSPECT'
                                checked={clientRadioSelected === 'NEW_PROSPECT'}
                                onChange={(event, data) => handleClientRadioSelected(data.value as string)}
                            />
                            <FormRadio
                                label='Cliente existente'
                                value='EXISTING_CLIENT'
                                checked={clientRadioSelected === 'EXISTING_CLIENT'}
                                onChange={(event, data) => handleClientRadioSelected(data.value as string)}
                            />
                            <Divider hidden />
                        </FormGroup>

                        {clientRadioSelected === 'EXISTING_CLIENT' && (
                            <SearchClientDropdown value={clientIdSelected} setValue={setClientIdSelected} />
                        )}

                        {clientRadioSelected === 'NEW_PROSPECT' && (
                            <>
                                <FormGroup widths='equal'>
                                    <FormField
                                        control={Input}
                                        label='Nombre'
                                        placeholder='Nombre(s)'
                                        name="name"
                                        value={requestPersonData.name}
                                        onChange={handleChangePersonData}
                                        required
                                    />
                                    <FormField
                                        control={Input}
                                        label='Apellido paterno'
                                        placeholder='Apellido paterno'
                                        name="paternalSurname"
                                        value={requestPersonData.paternalSurname}
                                        onChange={handleChangePersonData}
                                        required
                                    />
                                    <FormField
                                        control={Input}
                                        label='Apellido materno'
                                        placeholder='Apellido materno'
                                        name="maternalSurname"
                                        value={requestPersonData.maternalSurname}
                                        onChange={handleChangePersonData}
                                    />
                                    <FormField
                                        control={Select}
                                        label='Genero'
                                        options={GENDER_OPTIONS}
                                        placeholder='Selecciona una opción...'
                                        name="gender"
                                        value={requestPersonData.gender}
                                        onChange={handleChangePersonData}
                                        clearable
                                        required
                                    />
                                </FormGroup>
                                <FormGroup widths='equal'>
                                    <FormField
                                        width={4}
                                        control={Input}
                                        label='Telefono'
                                        placeholder='10 caracteres numericos sin espacios ni guiones'
                                        name="phone"
                                        value={requestPersonData.phone}
                                        onChange={handleChangePersonData}
                                        required
                                    />
                                    <FormField
                                        width={4}
                                        control={Select}
                                        label='Edad'
                                        options={AGE_RANGES_OPTIONS}
                                        placeholder='Selecciona una opción...'
                                        name="age"
                                        value={requestPersonData.age}
                                        onChange={handleChangePersonData}
                                        clearable
                                        required
                                    />
                                    <FormField
                                        width={2}
                                        control={Input}
                                        type='number'
                                        min='1'
                                        max='31'
                                        label='Dia'
                                        name="birthDay"
                                        placeholder='Cumpleaños'
                                        value={requestPersonData.birthDay}
                                        onChange={handleChangePersonData}
                                    />
                                    <FormField
                                        width={4}
                                        control={Select}
                                        label='Mes de cumpleaños'
                                        options={MONTH_OPTIONS}
                                        placeholder='Mes de cumpleaños'
                                        name="birthMonth"
                                        value={requestPersonData.birthMonth}
                                        onChange={handleChangePersonData}
                                        clearable
                                    />
                                    <FormField
                                        width={2}
                                        control={Input}
                                        type='number'
                                        min='1'
                                        max='9999'
                                        label='Año'
                                        placeholder='Año de nacimiento'
                                        name="birthYear"
                                        value={requestPersonData.birthYear}
                                        onChange={handleChangePersonData}
                                    />
                                </FormGroup>
                            </>
                        )}

                        <Divider hidden />
                        <Divider horizontal>
                            <Header as='h5'>
                                Selecciona el domicilio, fecha y hora para la cita
                            </Header>
                        </Divider>

                        <FormField required>
                            <label>Fecha y hora de visita</label>
                            <DatePicker
                                showTimeSelect
                                isClearable
                                minDate={new Date()}
                                minTime={setHours(setMinutes(new Date(), 59), 7)}
                                maxTime={setHours(setMinutes(new Date(), 59), 21)}
                                dateFormat="dd/MM/yyyy h:mm aa"
                                selected={appointmentDateTime}
                                placeholderText="Fecha de visita"
                                onChange={(date: Date | null, e) => setAppointmentDateTime(date)}
                                wrapperClassName="date-picker-custom-parent"
                                className="date-picker-custom"
                                name="appointment"
                            />
                        </FormField>

                        {clientRadioSelected && (
                            <>
                                <FormGroup inline>
                                    <FormRadio
                                        label='Nuevo domicilio'
                                        value='NEW_ADDRESS'
                                        checked={addressRadioSelected === 'NEW_ADDRESS'}
                                        onChange={(event, data) => handleAddressRadioSelected(data.value as string)}
                                    />
                                    {clientRadioSelected === 'EXISTING_CLIENT' && (
                                        <FormRadio
                                            label='Domicilio existente'
                                            value='EXISTING_ADDRESS'
                                            checked={addressRadioSelected === 'EXISTING_ADDRESS'}
                                            onChange={(event, data) => handleAddressRadioSelected(data.value as string)}
                                        />
                                    )}
                                    <FormRadio
                                        label='Showroom Luxon'
                                        value='SHOWROOM'
                                        checked={addressRadioSelected === 'SHOWROOM'}
                                        onChange={(event, data) => handleAddressRadioSelected(data.value as string)}
                                    />
                                </FormGroup>
                                <Divider hidden />
                            </>
                        )}

                        {addressRadioSelected === 'EXISTING_ADDRESS' && (
                            <FormField
                                control={Select}
                                label={<label htmlFor="form-select-control-advisor">Domicilios registrados del cliente seleccionado</label>}
                                options={clientAddresses}
                                placeholder="Selecciona una opcion"
                                name="selectedClientAddressId"
                                value={addressIdSelected}
                                onChange={handleSelectedClientAddress}
                                required
                                search
                                searchInput={{ id: "form-select-control-advisor" }}
                                clearable
                            />
                        )}

                        {addressRadioSelected === 'NEW_ADDRESS' && (
                            <>
                                <FormGroup widths='equal'>
                                    <FormField
                                        control={Input}
                                        label='Calle'
                                        placeholder='Nombre de la calle'
                                        name="street"
                                        value={requestAddressData?.street}
                                        onChange={handleChangeAddressData}
                                        required
                                    />
                                    <FormField
                                        control={Input}
                                        label='Numero exterior'
                                        placeholder='Numero exterior'
                                        name="exteriorNumber"
                                        value={requestAddressData?.exteriorNumber}
                                        onChange={handleChangeAddressData}
                                        required
                                    />
                                    <FormField
                                        control={Input}
                                        label='Numero interior'
                                        placeholder='Numero interior'
                                        name="interiorNumber"
                                        value={requestAddressData?.interiorNumber}
                                        onChange={handleChangeAddressData}
                                    />
                                </FormGroup>
                                <FormGroup widths='equal'>
                                    <FormField
                                        control={Input}
                                        label='Codigo postal'
                                        placeholder='Nombre de la calle'
                                        name="postalCode"
                                        value={requestAddressData?.postalCode}
                                        onChange={handleChangeAddressData}
                                        required
                                    />

                                    <FormField
                                        control={Select}
                                        label={<label htmlFor="form-select-control-neighborhood">Colonia</label>}
                                        options={neighborhoods}
                                        placeholder="Selecciona una opción"
                                        name="neighborhood"
                                        value={requestAddressData?.neighborhood}
                                        onChange={handleChangeAddressData}
                                        search
                                        searchInput={{ id: "form-select-control-neighborhood" }}
                                        clearable
                                        required
                                    />
                                </FormGroup>
                                <FormGroup widths='equal'>
                                    <FormField
                                        control={Input}
                                        label='Ciudad'
                                        placeholder='Ciudad'
                                        name="city"
                                        value={requestAddressData?.city}
                                        onChange={handleChangeAddressData}
                                        required
                                        readonly
                                    />
                                    <FormField
                                        control={Input}
                                        label='Municipio'
                                        placeholder='Municipio'
                                        name="municipality"
                                        value={requestAddressData?.municipality}
                                        onChange={handleChangeAddressData}
                                        required
                                        readonly
                                    />
                                    <FormField
                                        control={Input}
                                        label='Estado'
                                        placeholder='Estado'
                                        name="state"
                                        value={requestAddressData?.state}
                                        onChange={handleChangeAddressData}
                                        required
                                        readonly
                                    />
                                </FormGroup>
                            </>
                        )}

                    </div>
                </ModalContent>
                <ModalActions>
                    <Button onClick={(event) => handleCancelModal(event)}>
                        <Icon name="cancel" /> Cancelar
                    </Button>
                    <Button color="green" onClick={(e) => handleSubmit(e)} loading={isSubmitting} >
                        <Icon name="checkmark" /> Guardar
                    </Button>
                </ModalActions>
            </Modal>
        </>
    );
};

export default CreateNewProspectModal;
